/**
 * Enhanced Date Formatter Utility
 * Returns dates in "05 May 2025" and times in "8:00 AM" format
 */

const monthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Formats date as "05 May 2025"
 * @param {Date} date - Date object
 * @returns {string} Formatted date string
 */
const formatPrettyDate = (date) => {
  const day = date.getDate().toString().padStart(2, "0");
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

/**
 * Formats time as "8:00 AM"
 * @param {Date} date - Date object
 * @returns {string} Formatted time string
 */
const formatPrettyTime = (date) => {
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12; // Convert 0 to 12
  return `${hours}:${minutes} ${ampm}`;
};

/**
 * Parses Microsoft JSON date string like "/Date(1748390400000)/"
 * @param {string} input
 * @returns {Date|null}
 */
const parseMicrosoftDate = (input) => {
  const match = input.match(/\/Date\((\d+)\)\//);
  return match ? new Date(parseInt(match[1], 10)) : null;
};

/**
 * Parses compact date strings like "20220114" as YYYYMMDD
 * @param {string} input
 * @returns {Date|null}
 */
const parseCompactYYYYMMDD = (input) => {
  if (/^\d{8}$/.test(input)) {
    const year = parseInt(input.slice(0, 4), 10);
    const month = parseInt(input.slice(4, 6), 10) - 1;
    const day = parseInt(input.slice(6, 8), 10);
    return new Date(year, month, day);
  }
  return null;
};

/**
 * Parses ISO date strings like "2022-06-02"
 * @param {string} input
 * @returns {Date|null}
 */
const parseISODate = (input) => {
  // Date-only: "2022-06-02" — parse manually to avoid UTC-to-local date shift
  const dateOnly = input.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) {
    return new Date(parseInt(dateOnly[1]), parseInt(dateOnly[2]) - 1, parseInt(dateOnly[3]));
  }
  // Date with time but no timezone: "2022-06-02T00:00:00" — treat as local
  const dateTime = input.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/);
  if (dateTime) {
    return new Date(
      parseInt(dateTime[1]), parseInt(dateTime[2]) - 1, parseInt(dateTime[3]),
      parseInt(dateTime[4]), parseInt(dateTime[5]), parseInt(dateTime[6])
    );
  }
  return null;
};

/**
 * Main date formatting function
 * @param {*} input - Date input (string, number, Date, etc.)
 * @param {boolean} includeTime - Whether to include time in output
 * @returns {string} Formatted date string
 */
const formatDate = (input = new Date(), includeTime = false) => {
  try {
    let date = null;

    // Valid Date object
    if (input instanceof Date && !isNaN(input.getTime())) {
      date = input;
    }
    // Timestamp (number or numeric string)
    else if (typeof input === "number" || /^\d+$/.test(input)) {
      const numeric = typeof input === "number" ? input : parseInt(input, 10);

      // Handle compact format like 20220114
      if (input.toString().length === 8) {
        date = parseCompactYYYYMMDD(input.toString());
      } else {
        date = new Date(numeric);
      }
    }
    // Microsoft JSON format: /Date(1748390400000)/
    else if (typeof input === "string" && input.startsWith("/Date(")) {
      date = parseMicrosoftDate(input);
    }
    // 4. ISO 8601 (e.g., 2025-07-04T11:52:39.176Z)
    else if (typeof input === "string" && /^\d{4}-\d{2}-\d{2}T/.test(input)) {
      // If ISO string lacks timezone (Z or offset), treat as UTC
      const isUTC = /Z|[+-]\d{2}:?\d{2}$/.test(input);
      const dateString = isUTC ? input : `${input}Z`;
      
      const parsed = new Date(dateString);
      if (!isNaN(parsed.getTime())) {
        date = parsed;
      }
    }
    // ISO date string format: "2022-06-02" or "2022-06-02T00:00:00"
    else if (typeof input === "string" && /^\d{4}-\d{2}-\d{2}/.test(input)) {
      date = parseISODate(input);
    }
    // Other string formats
    else if (typeof input === "string") {
      // Try compact string first
      if (/^\d{8}$/.test(input)) {
        date = parseCompactYYYYMMDD(input);
      } else {
        const parsed = new Date(input);
        if (!isNaN(parsed.getTime())) {
          date = parsed;
        }
      }
    }

    if (!date || isNaN(date.getTime())) {
      throw new Error("Invalid date input");
    }

    return includeTime
      ? `${formatPrettyDate(date)}, ${formatPrettyTime(date)}`
      : formatPrettyDate(date);
  } catch (error) {
    console.error("Date formatting error:", error);
    return input || "-";
  }
};

export default formatDate;
